import React from 'react';
import {useNavigate} from 'react-router-dom';
import {m} from 'framer-motion';
// @mui
import {Typography, Button, Box, alpha, Card} from '@mui/material';
import {
  SearchOff as SearchOffIcon,
  Home as HomeIcon,
  ErrorOutline as ErrorIcon,
  ContactSupport as ContactIcon,
  LinkOff as LinkOffIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
// components
import {varBounce} from 'packages/common/components/animate';
import {RED, SLATE} from 'packages/common/theme/palette';

// ----------------------------------------------------------------------

/**
 * Vue d'erreur pour établissement non trouvé avec design premium
 *
 * @return {JSX.Element} Vue d'erreur pour établissement non trouvé
 */
export default function CompanyNotFoundView() {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
  };

  const primaryColor = '#078DEE';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${SLATE[50]} 0%, ${SLATE.lighter} 100%)`,
        py: 8,
        px: 3,
      }}
    >
      <Box sx={{maxWidth: '1400px', mx: 'auto'}}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {xs: '1fr', lg: '5fr 7fr'},
            gap: 8,
            alignItems: 'center',
            minHeight: '70vh',
          }}
        >
          {/* Partie gauche - Hero */}
          <Box sx={{textAlign: {xs: 'center', lg: 'left'}}}>
            <m.div variants={varBounce().in}>
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '24px',
                  background: `linear-gradient(135deg, ${primaryColor}15, ${primaryColor}25)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: {xs: 'auto', lg: 0},
                  mb: 4,
                  border: `2px solid ${alpha(primaryColor, 0.1)}`,
                }}
              >
                <SearchOffIcon sx={{fontSize: 48, color: primaryColor}} />
              </Box>
            </m.div>

            <m.div variants={varBounce().in}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: {xs: '2.25rem', md: '3rem'},
                  fontWeight: 800,
                  color: 'grey.900',
                  mb: 4,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                }}
              >
                Établissement
                <br />
                <Box component="span" sx={{color: primaryColor}}>
                  introuvable
                </Box>
              </Typography>
            </m.div>


            <m.div variants={varBounce().in}>
              <Button
                onClick={handleGoHome}
                variant="contained"
                size="large"
                startIcon={<HomeIcon />}
                sx={{
                  'backgroundColor': primaryColor,
                  'color': 'white',
                  'px': 4,
                  'py': 1.5,
                  'borderRadius': '12px',
                  'fontWeight': 600,
                  'fontSize': '1rem',
                  'textTransform': 'none',
                  'boxShadow': `0 4px 20px ${alpha(primaryColor, 0.3)}`,
                  '&:hover': {
                    backgroundColor: alpha(primaryColor, 0.9),
                    transform: 'translateY(-1px)',
                    boxShadow: `0 8px 25px ${alpha(primaryColor, 0.4)}`,
                  },
                  'transition': 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                Retour à l&apos;accueil
              </Button>
            </m.div>
          </Box>

          {/* Partie droite - Solutions */}
          <Box>
            <m.div variants={varBounce().in}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: {xs: '1.75rem', md: '2.25rem'},
                  fontWeight: 700,
                  color: 'grey.900',
                  mb: 6,
                  textAlign: {xs: 'center', lg: 'left'},
                }}
              >
                Que s&apos;est-il passé ?
              </Typography>
            </m.div>

            <m.div variants={varBounce().in}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
                  gap: 3,
                }}
              >
                {[
                  {
                    icon: <ErrorIcon sx={{fontSize: 24}} />,
                    title: 'Erreur de saisie',
                    description: 'Vérifiez l\'orthographe du nom dans l\'URL',
                    color: RED[500],
                  },
                  {
                    icon: <ContactIcon sx={{fontSize: 24}} />,
                    title: 'Demandez le lien',
                    description: 'Contactez l\'établissement directement',
                    color: '#8b5cf6',
                  },
                  {
                    icon: <LinkOffIcon sx={{fontSize: 24}} />,
                    title: 'Lien obsolète',
                    description: 'L\'établissement a quitté notre plateforme',
                    color: '#f59e0b',
                  },
                  {
                    icon: <RefreshIcon sx={{fontSize: 24}} />,
                    title: 'Problème technique',
                    description: 'Réessayez dans quelques instants',
                    color: '#06b6d4',
                  },
                ].map((item, index) => (
                  <Card
                    key={index}
                    elevation={0}
                    sx={{
                      'p': 4,
                      'background': 'white',
                      'border': `1px solid ${alpha(item.color, 0.1)}`,
                      'borderRadius': '16px',
                      'position': 'relative',
                      'transition': 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 40px ${alpha(item.color, 0.15)}`,
                        borderColor: alpha(item.color, 0.2),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        backgroundColor: alpha(item.color, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: item.color,
                        mb: 3,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: 'grey.900',
                        mb: 1,
                        fontSize: '1.1rem',
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'grey.600',
                        lineHeight: 1.6,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Card>
                ))}
              </Box>
            </m.div>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
